import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Command } from 'lucide-react';
import { getAllRoutes } from '../config/menuConfig';

// Todas las rutas planas del menuConfig
const ALL_ROUTES = getAllRoutes();

// Agrupa rutas por sección padre
const grouped = ALL_ROUTES.reduce((acc, route) => {
  const key = route.parent || 'General';
  if (!acc[key]) acc[key] = [];
  acc[key].push(route);
  return acc;
}, {});

export function CommandSearch({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  // Filtra rutas según query
  const filtered = query.trim()
    ? ALL_ROUTES.filter(
        (r) =>
          r.title.toLowerCase().includes(query.toLowerCase()) ||
          (r.parent && r.parent.toLowerCase().includes(query.toLowerCase())) ||
          r.path.toLowerCase().includes(query.toLowerCase())
      )
    : null; // null = mostrar todo agrupado

  const flatList = filtered || ALL_ROUTES;

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = useCallback(
    (path) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose]
  );

  // Navegación con teclado
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, flatList.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter' && flatList[activeIdx]) {
        handleSelect(flatList[activeIdx].path);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, flatList, activeIdx, handleSelect, onClose]);

  // Reset activeIdx cuando cambia query
  useEffect(() => { setActiveIdx(0); }, [query]);

  if (!open) return null;

  return (
    <div
      className="yool-cmd-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="yool-cmd-panel">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            className="yool-cmd-input flex-1 p-0 border-none"
            placeholder="Buscar páginas, secciones…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono
                          bg-slate-100 dark:bg-slate-800 text-slate-500 rounded border border-slate-200
                          dark:border-slate-700">
            <Command className="h-2.5 w-2.5" />K
          </kbd>
        </div>

        {/* Resultados */}
        <div className="yool-cmd-list">
          {filtered !== null ? (
            /* — Vista filtrada (lista plana) — */
            filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">
                No se encontraron resultados para «{query}»
              </p>
            ) : (
              filtered.map((route, idx) => (
                <RouteItem
                  key={route.path}
                  route={route}
                  isActive={idx === activeIdx}
                  onSelect={handleSelect}
                  onHover={() => setActiveIdx(idx)}
                />
              ))
            )
          ) : (
            /* — Vista agrupada (sin query) — */
            Object.entries(grouped).map(([section, routes]) => (
              <div key={section}>
                <div className="yool-cmd-section-label">{section}</div>
                {routes.map((route, idx) => {
                  const globalIdx = ALL_ROUTES.indexOf(route);
                  return (
                    <RouteItem
                      key={route.path}
                      route={route}
                      isActive={globalIdx === activeIdx}
                      onSelect={handleSelect}
                      onHover={() => setActiveIdx(globalIdx)}
                    />
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-end gap-3 px-4 py-2 border-t border-slate-100
                        dark:border-slate-800 text-[11px] text-slate-400">
          <span><kbd className="font-mono">↑↓</kbd> navegar</span>
          <span><kbd className="font-mono">↵</kbd> abrir</span>
          <span><kbd className="font-mono">Esc</kbd> cerrar</span>
        </div>
      </div>
    </div>
  );
}

function RouteItem({ route, isActive, onSelect, onHover }) {
  return (
    <button
      className={`yool-cmd-item w-full text-left ${isActive ? 'yool-cmd-item--active' : ''}`}
      onClick={() => onSelect(route.path)}
      onMouseEnter={onHover}
    >
      <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
      <span className="flex-1 truncate">{route.title}</span>
      {route.parent && (
        <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate max-w-[120px]">
          {route.parent}
        </span>
      )}
    </button>
  );
}

export default CommandSearch;
